import { createSelector } from '@reduxjs/toolkit';
import { get, isEmpty, isNil, sumBy } from 'lodash';
import { isBefore } from 'date-fns';
import {
  filterOutOptionalExtrasFromFerryReservation,
  getReservationsByProductType,
  processPassengerType,
} from 'utils/helpers';
import { getDateObjectFromReservation } from 'utils/helpers/reservations';
import { getTotalGiftCardAmount } from '../checkout';
import { getPassengerTypes } from '../types';

export const getCurrentBooking = (state) => state.booking?.current || {};
export const getOriginalBooking = (state) => state.booking?.original || {};
export const getUpcomingBooking = (state) => state.booking?.upcoming || {};

export const isBookingOneWay = createSelector(
  getCurrentBooking,
  (booking) =>
    get(
      booking,
      ['reservations_attributes', 0, 'inverse_reservation_id'],
      false // if we can't access this path, we dont want to assume its one way
    ) === null
);

export const getAmountToPay = createSelector(
  getCurrentBooking,
  (state) => state.checkout.paymentAmount,
  getTotalGiftCardAmount,
  (booking, paymentAmount, giftCardAmount) => {
    let amount = booking.deposit_outstanding_in_cents || 0;

    if (paymentAmount === 'balance') {
      amount = booking.balance_in_cents || 0;
    }

    return amount - giftCardAmount;
  }
);

export const getCommissionAmount = createSelector(
  getCurrentBooking,
  (booking) => booking.commission_in_cents
);

export const getBookingPaymentTypes = createSelector(
  getCurrentBooking,
  (booking) => booking.payment_types_attributes || []
);

export const getPayPalPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) =>
        paymentType.name === 'PayPal' && paymentType.gateway === 'braintree'
    )
);

export const getApplePayPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.payment_method === 'apple_pay'
    )
);

export const getGooglePayPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.payment_method === 'google_pay'
    )
);

export const getGiftCardPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.gateway === 'national_giftcard'
    )
);

export const getPaymentTypeId = (state) => state.checkout.paymentTypeId;

// booking.surcharge_in_cents represents surcharges that HAVE BEEN PAID
export const getSurchargeInCents = createSelector(
  getCurrentBooking,
  (booking) => booking.surcharge_in_cents
);

export const getSelectedPaymentTypes = createSelector(
  getBookingPaymentTypes,
  getPaymentTypeId,
  (bookingPaymentTypes, paymentTypeId) =>
    bookingPaymentTypes.find(
      (paymentType) => paymentType.id === paymentTypeId
    ) || {}
);

export const getTransactionFee = createSelector(
  getSelectedPaymentTypes,
  (selectedPaymentType) => Number(selectedPaymentType?.transaction_fee || 0)
);

export const getSurchargeAmount = createSelector(
  getAmountToPay,
  getTransactionFee,
  (amountToPay, transactionFee) => {
    let surchargeAmount = 0;
    if (transactionFee !== 0) {
      surchargeAmount = amountToPay * (transactionFee / 100);
    }
    return surchargeAmount;
  }
);

export const getOptionalExtraReservations = createSelector(
  getCurrentBooking,
  (currentBooking) => {
    const { optionalExtras: cmsOptionalExtras } =
      ecom.craftConfig.bookingPreferences.optionalExtras || {};

    if (isEmpty(currentBooking) || !cmsOptionalExtras) {
      return [];
    }

    const optionalExtraRouteIds = cmsOptionalExtras
      .map((item) => item.routeIdOfCoach)
      .filter((item) => !isNil(item));
    const optionalExtraResIds = cmsOptionalExtras
      .map((item) => item.qtResourceId)
      .filter((item) => !isNil(item));

    return currentBooking.reservations_attributes.filter(
      ({ resource_id, route_id }) =>
        optionalExtraResIds.includes(resource_id) ||
        optionalExtraRouteIds.includes(route_id)
    );
  }
);

export const getOptionalExtras = createSelector(
  getCurrentBooking,
  (currentBooking) => {
    const { optionalExtras: cmsOptionalExtras } =
      ecom.craftConfig.bookingPreferences.optionalExtras || {};

    if (isEmpty(currentBooking) || !cmsOptionalExtras) {
      return [];
    }

    const optionalExtraRouteIds = cmsOptionalExtras
      .map((item) => item.routeIdOfCoach)
      .filter((item) => !isNil(item));
    const optionalExtraResIds = cmsOptionalExtras
      .map((item) => item.qtResourceId)
      .filter((item) => !isNil(item));

    const reservations = currentBooking.reservations_attributes;

    const itemsOfResource = reservations
      .filter(({ resource_id }) => optionalExtraResIds.includes(resource_id))
      .map((item) => {
        const {
          resource_id,
          quantity,
          gross_in_cents,
          pre_adjusted_commission_in_cents,
        } = item;
        const name = cmsOptionalExtras.find(
          ({ qtResourceId }) => qtResourceId === resource_id
        ).title;
        return {
          name,
          count: quantity,
          price: gross_in_cents,
          commissionPrice: pre_adjusted_commission_in_cents,
          resourceId: resource_id,
        };
      });

    const itemsOfCoach = reservations
      .filter(({ route_id }) => optionalExtraRouteIds.includes(route_id))
      .map((item) => {
        const {
          route_id,
          passenger_ids,
          gross_in_cents,
          pre_adjusted_commission_in_cents,
        } = item;
        const name = cmsOptionalExtras.find(
          ({ routeIdOfCoach }) => routeIdOfCoach === route_id
        ).title;
        return {
          name,
          count: passenger_ids.length,
          price: gross_in_cents,
          commissionPrice: pre_adjusted_commission_in_cents,
          resourceId: null,
        };
      });
    return [...itemsOfResource, ...itemsOfCoach];
  }
);

/*
  Steps to get prices for each passenger types and vehicles
    1. Get reservation ids
    2. Find each reservation in the reservations_attributes
    3. Sum up each passenger and vehicles
*/
export const getPrices = createSelector(
  getCurrentBooking,
  getPassengerTypes,
  getOptionalExtras,
  (currentBooking, qtPassengerTypes, optionalExtras) => {
    const passengerPrices = {};
    let vehiclePrice = 0;
    let commissionPrice = 0;
    let commissionPercentage = 0;
    let ferrySubtotalPrice = 0;

    if (!isEmpty(currentBooking)) {
      // Find each reservation in the reservations_attributes
      let ferryReservations = getReservationsByProductType(
        'ferry,transfer',
        currentBooking
      );
      ferryReservations = filterOutOptionalExtrasFromFerryReservation({
        reservations: ferryReservations,
      });
      // Sum up each passengers and vehicles
      ferryReservations.forEach((reservation) => {
        const passengerDetail = processPassengerType(
          reservation.passenger_splits,
          currentBooking.passengers_attributes,
          qtPassengerTypes
        );

        Object.keys(passengerDetail).forEach((key) => {
          const pricePerPassenger =
            reservation.consumer_grosses[passengerDetail[key].consumer_id];

          if (passengerPrices[key]) {
            passengerPrices[key] +=
              pricePerPassenger * passengerDetail[key].count;
          } else {
            passengerPrices[key] =
              pricePerPassenger * passengerDetail[key].count;
          }
        });

        reservation.vehicle_splits?.forEach((vehicle) => {
          vehiclePrice += reservation.consumer_grosses[vehicle.consumer_id];
        });

        if (!commissionPercentage) {
          commissionPercentage = Number(
            reservation.passenger_splits.find(
              (passenger) => Number(passenger.commission_percentage) !== 0
            )?.commission_percentage || 0
          );
        }

        commissionPrice += reservation.pre_adjusted_commission_in_cents;

        ferrySubtotalPrice += reservation.gross_in_cents;
      });
    }

    let optionalExtraPrice = 0;
    if (!isEmpty(optionalExtras)) {
      optionalExtraPrice = sumBy(optionalExtras, 'price');
      ferrySubtotalPrice += optionalExtraPrice;
      const commissionOfOptionalExtra = sumBy(
        optionalExtras,
        'commissionPrice'
      );
      commissionPrice += commissionOfOptionalExtra;
    }

    // Don't show $0.00 on the pages
    if (ferrySubtotalPrice === 0) {
      ferrySubtotalPrice = undefined;
    }
    if (vehiclePrice === 0) {
      vehiclePrice = undefined;
    }
    if (commissionPrice === 0) {
      commissionPrice = undefined;
    } else {
      ferrySubtotalPrice -= commissionPrice;
    }

    return {
      passengerPrices,
      ferrySubtotalPrice,
      vehiclePrice,
      commissionPrice,
      commissionPercentage,
      optionalExtraPrice,
    };
  }
);

export const getGiftVoucherReservations = createSelector(
  getCurrentBooking,
  (booking) => {
    const reservations = booking.reservations_attributes || [];

    return reservations.filter((reservation) =>
      reservation.product_name_underscore.includes('gift_vouchers')
    );
  }
);

export const getNextUpcomingReservation = createSelector(
  getUpcomingBooking,
  (booking) => {
    if (!booking.reservations_attributes?.length) {
      return;
    }

    const now = new Date();
    const upcomingReservation = booking.reservations_attributes.find(
      (reservation) => {
        const travelTime = getDateObjectFromReservation(reservation);

        return isBefore(now, travelTime);
      }
    );

    return upcomingReservation;
  }
);
